import { AppInitService, AuthenticationService } from '@core/services';
import { CanActivateFn, CanMatchFn, Data, Route, Router } from '@angular/router';
import { DASHBOARD_PATH, LOGIN_PATH } from '@core/constants';
import { filter, map, switchMap, take } from 'rxjs';

import { inject } from '@angular/core';

// Metadata supportati:
// data: {
//   requiresAuth?: boolean;   // rotta accessibile solo se autenticato
//   guestOnly?: boolean;      // rotta accessibile solo se NON autenticato (es. login, registrazione)
//   roles?: string[];         // opzionale: ruoli ammessi
//   redirectAuth?: string;    // override default redirect quando guestOnly e autenticato (default '/dashboard')
//   redirectGuest?: string;   // override default redirect quando requiresAuth e non autenticato (default '/login')
// }
// Funzione riutilizzata da CanActivate & CanMatch
function evaluateAccess(routeData: Data | undefined, user: unknown, router: Router): boolean {
  const data = routeData ?? {};
  const requiresAuth = data['requiresAuth'] === true;
  const guestOnly = data['guestOnly'] === true;
  const roles = data['roles'] as string[] | undefined;
  const redirectAuth = (data['redirectAuth'] as string) || DASHBOARD_PATH;
  const redirectGuest = (data['redirectGuest'] as string) || LOGIN_PATH;

  // Richiede autenticazione ma utente assente
  if (requiresAuth && !user) {
    router.navigate([redirectGuest]);
    return false;
  }
  // Solo guest ma utente presente
  if (guestOnly && user) {
    router.navigate([redirectAuth]);
    return false;
  }
  // Controllo ruoli (placeholder: utente con proprietà role opzionale)
  if (roles && roles.length && user) {
    const maybeRole = (user as { role?: string }).role;
    if (!maybeRole || !roles.includes(maybeRole)) {
      router.navigate([redirectAuth]);
      return false;
    }
  }
  return true;
}

export const routeAccessGuard: CanActivateFn = route => {
  const auth = inject(AuthenticationService);
  const router = inject(Router);
  const appInit = inject(AppInitService);

  // Aspetta che l'inizializzazione sia completata prima di valutare l'accesso
  return appInit.initialized$.pipe(
    filter(initialized => initialized),
    take(1),
    switchMap(() =>
      auth.currentUser$.pipe(
        take(1),
        map(user => evaluateAccess(route.data, user, router))
      )
    )
  );
};

// CanMatch: evita il caricamento di segmenti lazy se non soddisfatti i vincoli di accesso
export const routeAccessMatch: CanMatchFn = (route: Route) => {
  const auth = inject(AuthenticationService);
  const router = inject(Router);
  const appInit = inject(AppInitService);

  // Aspetta che l'inizializzazione sia completata prima di valutare l'accesso
  return appInit.initialized$.pipe(
    filter(initialized => initialized),
    take(1),
    switchMap(() =>
      auth.currentUser$.pipe(
        take(1),
        map(user => evaluateAccess(route.data, user, router))
      )
    )
  );
};
